import { useState, useEffect, useRef } from "react";
import MainView from "views/MainView";
import {
  meterDetails,
  updateRemark,
  _H5Event,
  handleFromAppParams,
  queryStrToObject,
  determineBPColor,
} from "request";
import { useIntlContext, SupportedLocales } from "views/IntlContext";
import { Spinner } from "components";

import { PointBox, IPostion, Remark } from "./components";

interface IMeterData {
  sys: number;
  dia: number;
  pul: number;
}

const defaultMeterData = {
  sys: 0,
  dia: 0,
  pul: 0,
};

export const DetailsPage = () => {
  const { switchLanguage, locale } = useIntlContext();

  const [meterData, setMeterData] = useState<IMeterData>(defaultMeterData);
  const [pointPostion, setPointPostion] = useState<IPostion>({
    left: 0,
    bottom: 0,
  });
  const [currentColor, setCurrentColor] = useState<string>("#333");
  const [remark, setRemark] = useState<string>("");
  const [isShow, setIsShow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isFetchLoading, setIsFetchLoading] = useState<boolean>(true);

  const appParams = useRef<any>(null);

  useEffect(() => {
    sumPointWH(defaultMeterData.dia, defaultMeterData.sys);
  }, []);
  const sumPointWH = async (dia: number, sys: number) => {
    const pointWidth: number = 10;
    const NEScale = 1;

    pointLeft(dia, pointWidth, NEScale);
    pointTop(sys, pointWidth, NEScale);
  };

  const getSignDataDetails = async (id: number, deviceId: string) => {
    setIsFetchLoading(true);
    const res: any = await meterDetails({ params: { id, deviceId } });
    setIsFetchLoading(false);
    if (res.code === "200" && res.result) {
      const {
        pul = 0,
        sys = 0,
        dia = 0,
        dateStr,
        remark: remarkContent,
      } = res.result;

      setMeterData({
        sys,
        pul,
        dia,
      });

      setCurrentColor(determineBPColor(dia, sys));
      sumPointWH(dia, sys);
      if (!remarkContent) {
        setIsEdit(true);
      } else {
        setRemark(remarkContent);
      }
      if (dateStr) {
        document.title = dateStr;
      }
    }
  };

  const check = (
    variable: number,
    array: number[]
  ): { min_index: number; max_index: number } => {
    let min_index: number = 0; // 区间最小值下标
    let max_index: number = 1; // 区间最大值下标

    if (variable > array[array.length - 1]) {
      min_index = array.length - 2;
      max_index = array.length - 1;
      return { max_index, min_index };
    }

    for (let i = 0; i < array.length - 1; i++) {
      const current = array[i];
      const next = array[i + 1];
      if (variable >= current && variable <= next) {
        max_index = i + 1;
        min_index = i;
        break;
      }
    }

    return { max_index, min_index };
  };

  const pointLeft = (
    dia: number,
    pointWidth: number,
    NEScale: number
  ): void => {
    const xValues: number[] = [0, 80, 85, 90, 100, 110, 120];
    const xWidthValues: number[] = [0, 102, 122, 142, 182, 222, 262];
    const lenMax: number = xValues.length - 1;
    const pointWidthScaled: number = pointWidth * NEScale;

    const { max_index, min_index } = check(dia, xValues);
    const diaMin: number = xValues[min_index];
    const diaMax: number = xValues[max_index];
    const xMin: number = xWidthValues[min_index] * NEScale;
    const xMax: number = xWidthValues[max_index] * NEScale;

    let left: number = 0;

    if (dia >= xValues[lenMax]) {
      left = xMax - pointWidthScaled;
    } else if (dia < xValues[0]) {
      left = 0;
    } else {
      left =
        xMin +
        ((xMax - xMin) * (dia - diaMin)) / (diaMax - diaMin) -
        pointWidthScaled / 2;
    }
    setPointPostion((prev) => ({ ...prev, left }));
  };

  function pointTop(sys: number, pointWidth: number, NEScale: number): void {
    const yValues: number[] = [0, 120, 130, 140, 160, 180, 200];
    const yHeightValues: number[] = [0, 68, 88, 108, 148, 188, 228];
    const lenMax: number = yValues.length - 1;
    const pointWidthScaled: number = pointWidth * NEScale;

    const { max_index, min_index } = check(sys, yValues);
    const sysMin: number = yValues[min_index];
    const sysMax: number = yValues[max_index];
    const yMin: number = yHeightValues[min_index] * NEScale;
    const yMax: number = yHeightValues[max_index] * NEScale;

    let top: number = 0;

    if (sys >= yValues[lenMax]) {
      top = yMax - pointWidthScaled;
    } else if (sys < yValues[0]) {
      top = 0;
    } else {
      top =
        yMin +
        ((yMax - yMin) * (sys - sysMin)) / (sysMax - sysMin) -
        pointWidthScaled / 2;
    }

    setPointPostion((prev) => ({ ...prev, bottom: top }));
  }

  const onSubmit = (value: string) => {
    if (isLoading || !value || !value.trim()) return;
    setIsLoading(true);

    updateRemark({
      lang: locale,
      data: {
        id: appParams.current.id,
        remark: value,
      },
    })
      .then((response: any) => {
        setIsLoading(false);
        if (response.code === "200") {
          setRemark(value);
          setIsShow(true);
          setIsEdit(false);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const onToggleEdit = () => {
    setIsEdit((prev) => !prev);
  };

  useEffect(() => {
    if (isShow) {
      setTimeout(() => {
        setIsShow(false);
      }, 1500);
    }
  }, [isShow]);

  useEffect(() => {
    const queryData = queryStrToObject();
    if (queryData && Object.keys(queryData).length > 0) {
      appParams.current = queryData;
      getSignDataDetails(Number(queryData.id), queryData.deviceId);
      switchLanguage?.(queryData.lang as SupportedLocales);
    } else {
      const datas = {
        event: "GetDetailsParams",
        pageName: "details",
        callback: (responseStr: any) => {
          const jsonData = handleFromAppParams(responseStr);
          console.log("jsonData", jsonData);
          appParams.current = jsonData;
          switchLanguage?.(jsonData.lang as SupportedLocales);
          getSignDataDetails(jsonData.id, jsonData.deviceId);
        },
      };
      _H5Event(datas);
    }
  }, []);

  return (
    <MainView backgroundClassName="bg-white dark:bg-black1b1f1e pt-3">
      {isFetchLoading ? (
        <div className="w-full h-[100vh] flex items-center justify-center">
          <Spinner miniSmall className={{ "border-primary": true }} />
        </div>
      ) : (
        <div className="pt-[60px] px-[25px]">
          <div className="flex items-center flex-row justify-between max-w-[330px]">
            <div className="flex flex-col text-xs text-gray999 dark:text-white80 items-center">
              <span>SYS/DIA (mmHg)</span>
              <span
                className="text-[36px]  font-bold pt-[14px] pb-[24px] font-DINCond"
                style={{ color: currentColor }}
              >
                <span> {meterData.sys}</span><span>/</span><span>{meterData.dia}</span>
              </span>
            </div>
            <div className="flex flex-col text-xs text-gray999 dark:text-white80 items-center">
              <span>PUL</span>
              <span className="text-[36px] font-bold pt-[14px] pb-[24px] font-DINCond text-black333 dark:text-white">
                {meterData.pul}
              </span>
            </div>
          </div>
          <PointBox pointPostion={pointPostion} />
          <Remark
            remark={remark}
            onSubmit={onSubmit}
            onToggleEdit={onToggleEdit}
            isLoading={isLoading}
            isShow={isShow}
            isEdit={isEdit}
          />
        </div>
      )}
    </MainView>
  );
};
